import React, { useEffect, useState, useCallback } from "react";
import Topbar from "../components/Topbar";
import { api, formatApiError, API_BASE, tokenStorage } from "../lib/api";
import { toast } from "sonner";
import { Award, ExternalLink, Download, Ban, RotateCcw, Loader2, Search } from "lucide-react";
import { Input } from "../components/ui/input";

export default function AdminCertificates() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [confirmRevoke, setConfirmRevoke] = useState(null);
  const [revokeReason, setRevokeReason] = useState("");

  // Forzatura emissione (salta il questionario) — solo admin.
  const [forceOpen, setForceOpen] = useState(false);
  const [uq, setUq] = useState("");
  const [users, setUsers] = useState([]);
  const [selUser, setSelUser] = useState(null);
  const [issuable, setIssuable] = useState([]);
  const [forcing, setForcing] = useState(false);

  const resetForce = () => { setForceOpen(false); setSelUser(null); setUsers([]); setUq(""); setIssuable([]); };
  const searchUsers = async (q) => {
    setUq(q);
    if (!q.trim()) { setUsers([]); return; }
    try { const { data } = await api.get("/admin/users", { params: { search: q.trim(), limit: 8 } }); setUsers(data.items || []); }
    catch (e) { /* silent */ }
  };
  const pickUser = async (u) => {
    setSelUser(u); setIssuable([]);
    try { const { data } = await api.get("/admin/certificates/issuable", { params: { user_id: u.id } }); setIssuable(data || []); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const forceIssue = async (course_id) => {
    setForcing(true);
    try {
      await api.post("/admin/certificates/issue", { user_id: selUser.id, course_id });
      toast.success("Attestato emesso (forzato).");
      resetForce();
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setForcing(false); }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/certificates");
      setItems(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const download = async (cert) => {
    try {
      const res = await fetch(`${API_BASE}/admin/certificates/${cert.id}/download`, {
        headers: { Authorization: `Bearer ${tokenStorage.get()}` },
      });
      if (!res.ok) throw new Error("Download fallito");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${cert.code}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(e.message || "Errore download");
    }
  };

  const revoke = async () => {
    if (!confirmRevoke) return;
    if (!revokeReason.trim()) { toast.error("Motivo obbligatorio"); return; }
    try {
      await api.post(`/admin/certificates/${confirmRevoke.id}/revoke`, { reason: revokeReason });
      toast.success("Certificato revocato");
      setConfirmRevoke(null); setRevokeReason("");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const reissue = async (cert) => {
    if (!window.confirm(`Riabilitare il certificato ${cert.code}?`)) return;
    try {
      await api.post(`/admin/certificates/${cert.id}/reissue`);
      toast.success("Certificato riabilitato");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const filtered = items.filter((c) => {
    if (!search.trim()) return true;
    const q = search.trim().toLowerCase();
    return (
      c.code.toLowerCase().includes(q) ||
      (c.user_email || "").toLowerCase().includes(q) ||
      `${c.first_name} ${c.last_name}`.toLowerCase().includes(q) ||
      c.course_title.toLowerCase().includes(q)
    );
  });

  return (
    <div data-testid="admin-certificates-page" className="min-h-screen bg-brand-bg">
      <Topbar />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="mb-8">
          <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-brand-gold mb-2">
            <Award size={14} /> Pannello Super Admin
          </div>
          <h1 className="text-3xl sm:text-4xl text-brand-ink mb-1">Certificati emessi</h1>
          <p className="text-sm text-brand-muted">{items.length} certificat{items.length === 1 ? "o" : "i"} totali.</p>
        </div>

        <div className="flex items-center gap-3 mb-4">
          <div className="relative max-w-md flex-1">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-brand-muted" />
            <Input
              data-testid="cert-search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Cerca per codice, email, nome, corso…"
              className="pl-9 h-10 rounded-sm"
            />
          </div>
          <button
            onClick={() => setForceOpen(true)}
            data-testid="cert-force-btn"
            className="btn-primary inline-flex items-center gap-2 shrink-0"
          >
            <Award size={16} /> Forza emissione
          </button>
        </div>

        <div className="bg-white border border-brand-line rounded-sm overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-brand-bg border-b border-brand-line text-brand-muted">
              <tr>
                <th className="text-left px-4 py-3 font-semibold">Codice</th>
                <th className="text-left px-4 py-3 font-semibold">Studente</th>
                <th className="text-left px-4 py-3 font-semibold">Corso</th>
                <th className="text-left px-4 py-3 font-semibold">Emesso il</th>
                <th className="text-left px-4 py-3 font-semibold">Stato</th>
                <th className="text-right px-4 py-3 font-semibold">Azioni</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-brand-line" data-testid="certs-tbody">
              {loading ? (
                <tr><td colSpan={6} className="text-center py-10 text-brand-muted">
                  <Loader2 size={18} className="inline animate-spin mr-2" /> Caricamento…
                </td></tr>
              ) : filtered.length === 0 ? (
                <tr><td colSpan={6} className="text-center py-10 text-brand-muted">Nessun certificato.</td></tr>
              ) : filtered.map((c) => (
                <tr key={c.id} data-testid={`cert-row-${c.code}`}>
                  <td className="px-4 py-3 font-mono text-xs text-brand-blue">{c.code}</td>
                  <td className="px-4 py-3">
                    <div className="text-brand-ink">{c.first_name} {c.last_name}</div>
                    <div className="text-xs text-brand-muted">{c.user_email}</div>
                  </td>
                  <td className="px-4 py-3 text-brand-ink">{c.course_title}</td>
                  <td className="px-4 py-3 text-xs text-brand-muted">{new Date(c.issued_at).toLocaleDateString("it-IT")}</td>
                  <td className="px-4 py-3">
                    {c.is_revoked ? (
                      <span className="text-xs font-semibold text-red-700 bg-red-50 border border-red-200 rounded-sm px-2 py-0.5">Revocato</span>
                    ) : (
                      <span className="text-xs font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-sm px-2 py-0.5">Valido</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="inline-flex gap-1">
                      <a
                        href={`/verifica/${c.code}`}
                        target="_blank"
                        rel="noreferrer"
                        data-testid={`cert-view-public-${c.code}`}
                        title="Vedi pagina pubblica"
                        className="p-1.5 rounded-sm border border-brand-line hover:border-brand-blue hover:text-brand-blue text-brand-muted inline-flex"
                      >
                        <ExternalLink size={14} />
                      </a>
                      <button
                        onClick={() => download(c)}
                        data-testid={`cert-download-${c.code}`}
                        title="Scarica PDF"
                        className="p-1.5 rounded-sm border border-brand-line hover:border-brand-blue hover:text-brand-blue text-brand-muted"
                      >
                        <Download size={14} />
                      </button>
                      {c.is_revoked ? (
                        <button
                          onClick={() => reissue(c)}
                          data-testid={`cert-reissue-${c.code}`}
                          title="Riabilita"
                          className="p-1.5 rounded-sm border border-brand-line hover:border-emerald-500 hover:text-emerald-600 text-brand-muted"
                        >
                          <RotateCcw size={14} />
                        </button>
                      ) : (
                        <button
                          onClick={() => { setConfirmRevoke(c); setRevokeReason(""); }}
                          data-testid={`cert-revoke-${c.code}`}
                          title="Revoca"
                          className="p-1.5 rounded-sm border border-brand-line hover:border-red-500 hover:text-red-600 text-brand-muted"
                        >
                          <Ban size={14} />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </main>

      {confirmRevoke && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="revoke-dialog">
          <div className="bg-white rounded-sm p-6 max-w-md w-full">
            <h2 className="wordmark text-brand-blue text-xl mb-2">Revocare {confirmRevoke.code}?</h2>
            <p className="text-sm text-brand-muted mb-4">Motivo della revoca (visibile solo agli admin):</p>
            <textarea
              data-testid="revoke-reason"
              value={revokeReason}
              onChange={(e) => setRevokeReason(e.target.value)}
              className="w-full min-h-[80px] rounded-sm border border-brand-line p-2 text-sm mb-4"
              placeholder="Es. Errore di rilascio, dati anagrafici errati…"
            />
            <div className="flex justify-end gap-2">
              <button onClick={() => setConfirmRevoke(null)} className="px-4 py-2 text-sm text-brand-muted">Annulla</button>
              <button onClick={revoke} data-testid="revoke-confirm" className="bg-red-600 hover:bg-red-700 text-white rounded-sm px-4 py-2 text-sm font-semibold">
                Revoca
              </button>
            </div>
          </div>
        </div>
      )}

      {forceOpen && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="force-dialog">
          <div className="bg-white rounded-sm p-6 max-w-md w-full">
            <h2 className="text-xl text-brand-blue mb-1">Forza emissione attestato</h2>
            <p className="text-sm text-brand-muted mb-4">Emette l'attestato saltando il questionario di gradimento. Cerca lo studente, poi scegli il corso.</p>
            {!selUser ? (
              <>
                <Input value={uq} onChange={(e) => searchUsers(e.target.value)} placeholder="Cerca per nome o email…" className="h-10 rounded-sm mb-2" />
                <div className="max-h-56 overflow-y-auto divide-y divide-brand-line border border-brand-line rounded-sm">
                  {users.length === 0 ? (
                    <div className="p-3 text-sm text-brand-muted">Digita per cercare uno studente.</div>
                  ) : users.map((u) => (
                    <button key={u.id} onClick={() => pickUser(u)} className="w-full text-left px-3 py-2 hover:bg-brand-bg text-sm">
                      <div className="text-brand-ink">{u.first_name} {u.last_name}</div>
                      <div className="text-xs text-brand-muted">{u.email || u.username} · {u.role}</div>
                    </button>
                  ))}
                </div>
              </>
            ) : (
              <>
                <div className="flex items-center justify-between mb-3">
                  <div className="text-sm text-brand-ink">{selUser.first_name} {selUser.last_name}</div>
                  <button onClick={() => { setSelUser(null); setIssuable([]); }} className="text-xs font-semibold text-brand-blue hover:underline">Cambia</button>
                </div>
                {issuable.length === 0 ? (
                  <div className="text-sm text-brand-muted p-3 border border-brand-line rounded-sm">Nessun corso emettibile (serve il test finale superato e nessun attestato già valido).</div>
                ) : (
                  <div className="space-y-2">
                    {issuable.map((c) => (
                      <div key={c.course_id} className="flex items-center justify-between gap-2 border border-brand-line rounded-sm px-3 py-2">
                        <span className="text-sm text-brand-ink truncate">{c.course_title}</span>
                        <button onClick={() => forceIssue(c.course_id)} disabled={forcing} className="btn-primary h-8 px-3 text-sm inline-flex items-center gap-1.5 disabled:opacity-60">
                          {forcing ? <Loader2 size={14} className="animate-spin" /> : "Forza"}
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}
            <div className="flex justify-end mt-4">
              <button onClick={resetForce} className="px-4 py-2 text-sm text-brand-muted">Chiudi</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
